"use client";

import React from "react";
import { MapPin, Mail, Phone, MessageCircle, Loader, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const textareaClasses = "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
function ConsultFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
      </div>;
  }
  const inquiryOptions = ["Trademark Acquisition", "Trademark Monetization", "Brand Strategy Guidance", "Legal Coordination", "Other"];
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="John Doe" className={inputClasses} />
              </FormControl>
              <FormDescription>Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="john@example.com" className={inputClasses} />
              </FormControl>
              <FormDescription>We'll use this to get back to you.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^\+?[0-9\s\-()]{7,20}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+91 98765 43210" className={inputClasses} />
              </FormControl>
              <FormDescription>Optional – for quicker follow-up.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Acme Startup Pvt. Ltd." className={inputClasses} />
              </FormControl>
              <FormDescription>Your business name (if applicable).</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>What are you interested in?</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {inquiryOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Your Message</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Briefly describe your requirement or question..." className={textareaClasses} />
            </FormControl>
            <FormDescription>
              Provide any details that will help our advisors assist you.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold bg-foreground text-primary hover:bg-foreground/90 transition-colors">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Submitting...
          </> : "Submit Inquiry"}
      </Button>
    </Form>;
}
export default function ConsultOurAdvisors() {
  return <section id="consult-our-advisors" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                Get in Touch
              </h2>
              <p className="text-lg text-muted-foreground">
                Connect with our advisors in Delhi to buy or monetize your brand assets. We ensure legal clarity and fast execution.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="mt-1 p-2.5 rounded-lg bg-primary/10 text-primary">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">Headquarters</h4>
                  <p className="text-muted-foreground mt-1">Delhi – 110018, India</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="mt-1 p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">Email Us</h4>
                  <p className="text-muted-foreground mt-1">
                    <Link to="mailto:support@business-tantra.com" className="hover:text-primary transition-colors">
                      support@business-tantra.com
                    </Link>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="mt-1 p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">Call Us</h4>
                  <p className="text-muted-foreground mt-1">
                    <Link to="tel:+918820000222" className="hover:text-primary transition-colors">
                      +91-8820000222
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Link to="https://wa.me/918820000222" newTab className="block w-full">
                <Button className="w-full h-14 text-base font-semibold bg-[#25D366] hover:bg-[#20bd5a] text-white border-2 border-primary shadow-lg transition-all hover:shadow-xl">
                  <MessageCircle className="mr-2 size-5" />
                  Chat on WhatsApp
                </Button>
              </Link>
            </div>

            <div className="rounded-xl overflow-hidden border border-border shadow-sm h-[250px] relative bg-muted">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224345.83923192776!2d77.06889754725782!3d28.52728034389636!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x52c2b7494e204dce!2sNew%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Business Tantra Location" className="absolute inset-0" />
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="p-6 sm:p-8 shadow-xl border-border/50 bg-card/50 backdrop-blur-sm">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground">Consult Our Advisors</h3>
                <p className="text-muted-foreground mt-2">
                  Fill out the form below and our team will get back to you within 24 hours.
                </p>
              </div>

              <FormProvider formId="consult_advisors_form_contact" sectionName="Consult Our Advisors" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Submit Inquiry" successMessage="Thank you for your inquiry. One of our premium brand advisors will contact you shortly.">
                <ConsultFormContent />
              </FormProvider>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}